import { useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { FormField, FormInput, FormSelect, useToast } from '@practium/ui';
import type { FormSelectOption } from '@practium/ui';
import { fetchCepAddress } from '@practium/features/api';
import { STATE_TO_UF } from '../../properties/utils/masks';
import type { CheckoutPersonalData } from '../types';
import { personalDataSchema, parseErrors } from '../validation';
import styles from '../checkoutShared.module.css';

const STATE_OPTIONS: FormSelectOption[] = Object.keys(STATE_TO_UF).map((name) => ({
	value: name,
	label: name,
}));

interface StepPersonalDataProps {
	data: CheckoutPersonalData;
	onChange: Dispatch<SetStateAction<CheckoutPersonalData>>;
}

export function maskCpf(value: string): string {
	const n = value.replace(/\D/g, '').slice(0, 11);
	if (n.length <= 3) return n;
	if (n.length <= 6) return `${n.slice(0, 3)}.${n.slice(3)}`;
	if (n.length <= 9) return `${n.slice(0, 3)}.${n.slice(3, 6)}.${n.slice(6)}`;
	return `${n.slice(0, 3)}.${n.slice(3, 6)}.${n.slice(6, 9)}-${n.slice(9)}`;
}

export function maskPhone(value: string): string {
	const n = value.replace(/\D/g, '').slice(0, 11);
	if (n.length <= 2) return n;
	if (n.length <= 7) return `(${n.slice(0, 2)}) ${n.slice(2)}`;
	return `(${n.slice(0, 2)}) ${n.slice(2, 7)}-${n.slice(7)}`;
}

function maskZip(value: string): string {
	const n = value.replace(/\D/g, '').slice(0, 8);
	if (n.length <= 5) return n;
	return `${n.slice(0, 5)}-${n.slice(5)}`;
}

export const StepPersonalData = ({ data, onChange }: StepPersonalDataProps) => {
	const toast = useToast();
	const [isCepLoading, setIsCepLoading] = useState(false);
	const [isStateEditable, setIsStateEditable] = useState(true);
	const [touched, setTouched] = useState<Set<string>>(new Set());
	const numberInputRef = useRef<HTMLInputElement>(null);

	const touch = (field: string) => setTouched((prev) => new Set([...prev, field]));

	const errors = parseErrors(personalDataSchema, data);
	const err = (field: string) => (touched.has(field) ? errors[field] : undefined);

	const set = (field: keyof CheckoutPersonalData, value: string) =>
		onChange({ ...data, [field]: value });

	const setAddress = (field: keyof CheckoutPersonalData['address'], value: string) =>
		onChange({ ...data, address: { ...data.address, [field]: value } });

	const handleCepChange = async (rawValue: string) => {
		const masked = maskZip(rawValue);
		setAddress('zipCode', masked);

		if (masked.replace(/\D/g, '').length !== 8) return;

		setIsCepLoading(true);
		try {
			const result = await fetchCepAddress(masked);
			onChange((prev) => ({
				...prev,
				address: {
					...prev.address,
					street: result.street || prev.address.street,
					district: result.district || prev.address.district,
					city: result.city || prev.address.city,
					state: result.state || prev.address.state,
				},
			}));
			setIsStateEditable(!result.state);
			setTouched(
				(prev) =>
					new Set([
						...prev,
						'address.street',
						'address.district',
						'address.city',
						'address.state',
					])
			);
			toast.success('Endereço preenchido automaticamente pelo CEP.');
			numberInputRef.current?.focus();
		} catch {
			setIsStateEditable(true);
			toast.error('CEP não encontrado. Preencha o endereço manualmente.');
		} finally {
			setIsCepLoading(false);
		}
	};

	return (
		<div className={styles.formGrid}>
			<p className={styles.sectionHeader}>Dados Pessoais</p>

			<div className={styles.row2}>
				<FormField label="Nome" required error={err('name')}>
					<FormInput
						placeholder="João"
						value={data.name}
						hasError={!!err('name')}
						onChange={(e) => set('name', e.target.value)}
						onBlur={() => touch('name')}
					/>
				</FormField>
				<FormField label="Sobrenome" error={err('surName')}>
					<FormInput
						placeholder="Silva"
						value={data.surName}
						hasError={!!err('surName')}
						onChange={(e) => set('surName', e.target.value)}
						onBlur={() => touch('surName')}
					/>
				</FormField>
			</div>

			<div className={styles.row2}>
				<FormField label="CPF" required error={err('document')}>
					<FormInput
						placeholder="000.000.000-00"
						value={data.document}
						hasError={!!err('document')}
						onChange={(e) => set('document', maskCpf(e.target.value))}
						onBlur={() => touch('document')}
					/>
				</FormField>
				<FormField label="Telefone" error={err('phoneNumber')}>
					<FormInput
						placeholder="(00) 00000-0000"
						value={data.phoneNumber}
						hasError={!!err('phoneNumber')}
						onChange={(e) => set('phoneNumber', maskPhone(e.target.value))}
						onBlur={() => touch('phoneNumber')}
					/>
				</FormField>
			</div>

			<p className={styles.sectionHeader}>Endereço</p>

			<div className={styles.addressRow1}>
				<FormField label="CEP" required error={err('address.zipCode')}>
					<FormInput
						placeholder="00000-000"
						value={data.address.zipCode}
						hasError={!!err('address.zipCode')}
						onChange={(e) => handleCepChange(e.target.value)}
						onBlur={() => touch('address.zipCode')}
						disabled={isCepLoading}
					/>
				</FormField>
				<FormField label="Cidade" required error={err('address.city')}>
					<FormInput
						placeholder="São Paulo"
						value={data.address.city}
						hasError={!!err('address.city')}
						onChange={(e) => setAddress('city', e.target.value)}
						onBlur={() => touch('address.city')}
					/>
				</FormField>
				<FormField label="Estado" required error={err('address.state')}>
					<FormSelect
						options={STATE_OPTIONS}
						value={data.address.state}
						onChange={(v) => {
							setAddress('state', v);
							touch('address.state');
						}}
						placeholder="Selecione"
						searchable
						hasError={!!err('address.state')}
						disabled={!isStateEditable}
					/>
				</FormField>
			</div>

			<div className={styles.addressRow2}>
				<FormField label="Logradouro" required error={err('address.street')}>
					<FormInput
						placeholder="Rua Exemplo"
						value={data.address.street}
						hasError={!!err('address.street')}
						onChange={(e) => setAddress('street', e.target.value)}
						onBlur={() => touch('address.street')}
					/>
				</FormField>
				<FormField label="Número" required error={err('address.number')}>
					<FormInput
						ref={numberInputRef}
						placeholder="123"
						value={data.address.number}
						hasError={!!err('address.number')}
						onChange={(e) => setAddress('number', e.target.value)}
						onBlur={() => touch('address.number')}
					/>
				</FormField>
			</div>

			<div className={styles.addressRow3}>
				<FormField label="Bairro" required error={err('address.district')}>
					<FormInput
						placeholder="Centro"
						value={data.address.district}
						hasError={!!err('address.district')}
						onChange={(e) => setAddress('district', e.target.value)}
						onBlur={() => touch('address.district')}
					/>
				</FormField>
				<FormField label="Complemento" error={err('address.complement')}>
					<FormInput
						placeholder="Apto 101"
						value={data.address.complement}
						onChange={(e) => setAddress('complement', e.target.value)}
					/>
				</FormField>
			</div>
		</div>
	);
};
